import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Fade, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { RootState, AppDispatch } from '../../../../../Redux/store';
import { cyanBlueBackgroundButtonStyles } from '../../../../../styles/button';
import GroupConfiguration from './groupConfiguration';
import { SearchBar } from '../../../../../styles/textField';
import { buttonIconStyles } from '../../../../../styles/icon';
import {
  getGroupApps,
  getConfiguredPolicies,
  resetGroupConfiguration,
  updateGroupConfiguration,
  updateClose,
} from '../../../../../Redux/Slices/Application/AppConfiguration/Group/group';
import { deleteGroupConfiguration } from '../../../../../Redux/Slices/Application/AppConfiguration/Group/groupHandler';
import { handleSearch } from '../../../../../utils/Groups/groupUtility';
import CommonTable from '../../../../../components/Table/CommonTable';

function GroupSettingsTable() {
  const dispatch = useDispatch<AppDispatch>();
  const { editScreen, configuredGroups, app } = useSelector(
    (state: RootState) => state.application,
  );
  const { allPolicies } = useSelector(
    (state: RootState) => state.groupSettings,
  );

  const [filteredGroup, setFilteredGroup] = useState(configuredGroups);
  const [policyMap, setPolicyMap] = useState<Record<string, string>>({});

  useEffect(() => {
    if (allPolicies.length > 0) {
      const idToIdentifierMap: Record<string, string> = allPolicies.reduce(
        (map, obj) => {
          map[obj._id] = obj.identifier;
          return map;
        },
        {},
      );

      setPolicyMap(idToIdentifierMap);
    }
  }, [allPolicies]);

  const openGroupConfigurationScreen = async () => {
    dispatch(resetGroupConfiguration());
    dispatch(updateClose(true));
  };

  const handleEditGroup = async (id: string) => {
    configuredGroups.forEach(async (element: any) => {
      if (element._id === id) {
        dispatch(updateGroupConfiguration(element));
        dispatch(updateClose(true));
      }
    });
  };
  const handleDeleteGroup = async (id: string) => {
    await dispatch(deleteGroupConfiguration({ id, app }));
  };

  useEffect(() => {
    dispatch(getGroupApps());
    dispatch(getConfiguredPolicies());
  }, []);

  const transformApiResponse = (
    data: any[],
    policyMapping: Record<string, string>,
  ) => data.map((item) => ({
    identifier: item.identifier,
    description: item.description,
    id: item._id,
    policy: policyMapping[item.policy_id] || 'N/A',
  }));

  const transformedGroups = transformApiResponse(configuredGroups, policyMap);

  useEffect(() => {
    if (configuredGroups.length > 0 && Object.keys(policyMap).length > 0) {
      setFilteredGroup(transformApiResponse(configuredGroups, policyMap));
    }
  }, [configuredGroups, policyMap]);

  const styles = {
    height: { xxl: '30.938rem', xl: '18.75rem', md: '18.75rem' },
  };

  return (
    <Fade
      in={editScreen === 'GroupSettings'}
      {...(editScreen === 'GroupSettings' ? { timeout: 1000 } : {})}
    >
      <Box>
        <GroupConfiguration />
        <Box display="flex" justifyContent="space-between" p="1rem" pt="1.5rem">
          <SearchBar
            placeholder="Search for Identifiers"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleSearch(event, transformedGroups, setFilteredGroup)}
            sx={{
              maxWidth: {
                xxl: '25rem',
                xl: '20rem',
              },
            }}
          />
          <Button
            sx={{ ...cyanBlueBackgroundButtonStyles, width: '8rem' }}
            onClick={openGroupConfigurationScreen}
          >
            <AddIcon sx={{ ...buttonIconStyles, color: 'white' }} />
            &nbsp; Add New
          </Button>
        </Box>
        <Box>
          <CommonTable
            data={filteredGroup}
            columns={[
              { field: 'identifier', headerName: 'Identifiers' },
              { field: 'description', headerName: 'Description' },
              { field: 'policy', headerName: 'Policy' },
            ]}
            styles={styles}
            onEdit={handleEditGroup}
            onDelete={handleDeleteGroup}
            pages="Group"
          />
        </Box>
      </Box>
    </Fade>
  );
}

export default GroupSettingsTable;
